import React, { useEffect, useRef, useState } from 'react';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import type { CatalogModel } from './hooks/useAICatalog';

/** "163k" style token counts. */
export const formatTokens = (tokens: number): string =>
  tokens >= 1000 ? `${Math.round(tokens / 1000)}k` : String(tokens);

/** Share of the window in use, clamped to [0, 1]. */
export const contextFraction = (
  contextTokens: number,
  maxInputTokens: number
): number =>
  maxInputTokens > 0
    ? Math.min(1, Math.max(0, contextTokens / maxInputTokens))
    : 0;

/** "40%" for the meter and the panel. */
export const formatPercent = (fraction: number): string =>
  `${Math.round(fraction * 100)}%`;

/**
 * Fallback floor for "Compact now" when the catalog does not report the kept
 * window.
 */
const MIN_COMPACT_FRACTION = 0.1;

/**
 * Why a small chat cannot be compacted: compaction keeps the newest slice of
 * the conversation verbatim, so a chat that fits inside that slice has no
 * earlier history to summarize.
 */
const whyNothingToCompact = (
  model: Pick<CatalogModel, 'compactKeepTokens'>
): string => {
  const kept = model.compactKeepTokens
    ? `the newest ~${formatTokens(model.compactKeepTokens)} tokens`
    : 'the most recent conversation';
  return `Compacting summarizes the earlier conversation and keeps ${kept} as is, so it needs more history than this chat has so far.`;
};

/**
 * Why "Compact now" is unavailable on a chat this size, or null when it can
 * run.
 */
export const compactHint = (
  contextTokens: number,
  model: Pick<CatalogModel, 'maxInputTokens' | 'compactKeepTokens'>
): string | null => {
  const floor =
    model.compactKeepTokens ?? model.maxInputTokens * MIN_COMPACT_FRACTION;
  if (contextTokens >= floor) return null;
  return `Nothing to compact yet. ${whyNothingToCompact(model)}`;
};

/**
 * The line under "Compact now": why it is unavailable right now, or why the
 * last attempt was refused. A server refusal for a chat that still fits in the
 * kept window gets the same explanation as the client-side hint.
 */
export const compactStatusText = ({
  busy,
  compactError,
  hint,
  model,
}: {
  busy: boolean;
  compactError: string | null;
  hint: string | null;
  model: Pick<CatalogModel, 'compactKeepTokens'>;
}): string | null => {
  if (busy) return 'Available once the current response finishes.';
  if (compactError) {
    const sentence = compactError.replace(/\.?\s*$/, '.');
    return /nothing to compact/i.test(compactError)
      ? `${sentence} ${whyNothingToCompact(model)}`
      : sentence;
  }
  return hint;
};

/**
 * The box a `position: fixed` descendant of `el` is laid out against: the
 * viewport, unless an ancestor is transformed (the sliding chat sidebar, a
 * translated wrapper), in which case that ancestor.
 */
const fixedContainingBlock = (
  el: HTMLElement
): { right: number; bottom: number } => {
  let node: HTMLElement | null = el.parentElement;
  while (node) {
    const style = getComputedStyle(node);
    if (
      style.transform !== 'none' ||
      style.perspective !== 'none' ||
      style.filter !== 'none'
    ) {
      const rect = node.getBoundingClientRect();
      return { right: rect.right, bottom: rect.bottom };
    }
    node = node.parentElement;
  }
  return { right: window.innerWidth, bottom: window.innerHeight };
};

const RING_SIZE = 24;
const RING_STROKE = 3.5;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

interface ContextMeterProps {
  contextTokens: number;
  model: CatalogModel;
  compactionCount: number;
  /** The viewer owns the chat (only owners can compact). */
  canAct: boolean;
  /** A turn or compaction is in progress. */
  busy: boolean;
  compacting: boolean;
  /** Why the last "Compact now" was refused, shown until the next attempt. */
  compactError: string | null;
  onCompact: () => void;
}

/**
 * How full the chat's model context is, as a ring in the chat's bottom-right
 * corner. Clicking it opens the context window panel: usage against the window,
 * the automatic-compaction point, and the owner's "Compact now".
 */
const ContextMeter: React.FC<ContextMeterProps> = ({
  contextTokens,
  model,
  compactionCount,
  canAct,
  busy,
  compacting,
  compactError,
  onCompact,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  // The panel is fixed-positioned: right-aligned with the ring, and its bottom edge sits above the chat's input area
  // so it never covers the message box or the Send button (the ring itself lives in the footer below them).
  const [panelOffset, setPanelOffset] = useState<{
    right: number;
    bottom: number;
  } | null>(null);

  // Place the panel above the input area; follow window resizes while open.
  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const ring = rootRef.current;
      if (!ring) return;
      const ringRect = ring.getBoundingClientRect();
      const inputArea = ring
        .closest('.global-chat-sidebar')
        ?.querySelector('.ChatDisasterGPT-input-area');
      const anchorTop = inputArea
        ? inputArea.getBoundingClientRect().top
        : ringRect.top;
      const block = fixedContainingBlock(ring);
      setPanelOffset({
        right: Math.max(0, block.right - ringRect.right),
        bottom: Math.max(0, block.bottom - anchorTop + 8),
      });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  // Click outside or Escape closes the panel.
  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const fill = contextFraction(contextTokens, model.maxInputTokens);
  const trigger = contextFraction(
    model.compactTriggerTokens,
    model.maxInputTokens
  );
  const pastTrigger = fill >= trigger;
  const hint = compactHint(contextTokens, model);
  const statusText = compacting
    ? null
    : compactStatusText({ busy, compactError, hint, model });
  const summary = `${formatTokens(contextTokens)} / ${formatTokens(model.maxInputTokens)} tokens (${formatPercent(fill)})`;

  return (
    <div className="context-meter" ref={rootRef}>
      <button
        type="button"
        className={`context-meter__ring ${pastTrigger ? 'context-meter__ring--warn' : ''}`}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`Context window: ${summary}`}
        title={`Context window: ${summary}`}
        data-testid="chat-context-meter"
      >
        <svg
          width={RING_SIZE}
          height={RING_SIZE}
          viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
          aria-hidden="true"
          focusable="false"
        >
          <circle
            className="context-meter__track"
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            strokeWidth={RING_STROKE}
            fill="none"
          />
          {/* The group starts the arc at 12 o'clock; the circle's own CSS transform (the compacting spin) then
              rotates about its centre without disturbing that start. */}
          <g transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}>
            <circle
              className={`context-meter__fill ${compacting ? 'context-meter__fill--spinning' : ''}`}
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={RING_RADIUS}
              strokeWidth={RING_STROKE}
              fill="none"
              strokeLinecap="round"
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={
                RING_CIRCUMFERENCE * (1 - (compacting ? 0.25 : fill))
              }
            />
          </g>
        </svg>
      </button>

      {open && panelOffset && (
        <div
          className="context-meter__panel"
          style={{ right: panelOffset.right, bottom: panelOffset.bottom }}
          role="dialog"
          aria-label="Context window"
          data-testid="chat-context-panel"
        >
          <div className="context-meter__header">
            <span className="context-meter__title">Context window</span>
            <span className="context-meter__model">{model.name}</span>
          </div>

          <div
            className="context-meter__bar"
            role="meter"
            aria-label="Chat context used"
            aria-valuemin={0}
            aria-valuemax={model.maxInputTokens}
            aria-valuenow={contextTokens}
          >
            <div
              className={`context-meter__bar-fill ${pastTrigger ? 'context-meter__bar-fill--warn' : ''}`}
              style={{ width: `${fill * 100}%` }}
            />
            <div
              className="context-meter__bar-trigger"
              style={{ left: `${trigger * 100}%` }}
              title="Automatic compaction point"
            />
          </div>

          <div className="context-meter__row">
            <span>Tokens used</span>
            <span data-testid="chat-context-usage">
              {formatTokens(contextTokens)} /{' '}
              {formatTokens(model.maxInputTokens)} · {formatPercent(fill)}
            </span>
          </div>
          <div className="context-meter__row">
            <span>Compacts automatically at</span>
            <span>
              ~{formatTokens(model.compactTriggerTokens)} ·{' '}
              {formatPercent(trigger)}
            </span>
          </div>
          {!!model.compactKeepTokens && (
            <div className="context-meter__row">
              <span>Kept after compaction</span>
              <span>~{formatTokens(model.compactKeepTokens)} newest</span>
            </div>
          )}
          {compactionCount > 0 && (
            <div className="context-meter__row">
              <span>Compacted</span>
              <span>{compactionCount}×</span>
            </div>
          )}

          {canAct ? (
            <>
              <StylishNewButton
                className="button--secondary mt-2 w-100"
                type="button"
                onClick={onCompact}
                disabled={busy || compacting || !!hint}
                title={
                  hint ??
                  'Summarize the earlier conversation now to free up context'
                }
                data-testid="chat-compact-now"
              >
                {compacting ? (
                  <>
                    Compacting&nbsp;<i className="fa fa-spinner fa-pulse"></i>
                  </>
                ) : (
                  'Compact now'
                )}
              </StylishNewButton>
              {statusText && (
                <div
                  className="context-meter__hint"
                  data-testid="chat-compact-hint"
                >
                  {statusText}
                </div>
              )}
            </>
          ) : (
            <div className="context-meter__hint">
              Only the chat owner can compact this chat.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContextMeter;
